import { Badge, Banner, Button, Field, Input, InputArea, Select } from '@cloudflare/kumo';
import { ArrowRight, Flag, Lifebuoy, Sparkle, Tray, UsersThree, type Icon } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '../components/ui/DataTable';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { PageLayout } from '../components/ui/PageLayout';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

/**
 * Portal de suporte (estilo Cloudflare): cartões de entrada, chamados da organização e
 * base de conhecimento por tópico. Os chamados ficam no sistema e avisam o suporte por e-mail.
 */

interface Ticket {
  id: string; number: number; subject: string; description: string;
  category: string; priority: string; status: 'open' | 'resolved';
  createdBy: string | null; createdAt: string; resolvedAt: string | null;
}

const CATEGORIES = [
  { value: 'geral', label: 'Dúvida geral' }, { value: 'faturamento', label: 'Faturamento' },
  { value: 'whatsapp', label: 'WhatsApp' }, { value: 'ia', label: 'Agentes e IA' },
  { value: 'integracoes', label: 'Integrações' }, { value: 'bug', label: 'Problema no sistema' },
  { value: 'abuso', label: 'Denúncia de abuso' },
];
const PRIORITIES = [
  { value: 'low', label: 'Baixa' }, { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' }, { value: 'urgent', label: 'Urgente' },
];
const label = (list: Array<{ value: string; label: string }>, v: string) => list.find((i) => i.value === v)?.label ?? v;

const TOPICS: Array<{ title: string; links: Array<{ label: string; href: string }> }> = [
  { title: 'Primeiros passos', links: [{ label: 'Documentação', href: '/docs' }, { label: 'Conectar o WhatsApp', href: '/docs' }, { label: 'Importar contatos', href: '/docs' }] },
  { title: 'Agentes de IA', links: [{ label: 'Criar um agente', href: '/ia/agentes' }, { label: 'Base de conhecimento', href: '/ia/conhecimento' }, { label: 'Observabilidade', href: '/ia/observabilidade' }] },
  { title: 'Faturamento', links: [{ label: 'Plano e cobrança', href: '/faturamento' }, { label: 'Uso do mês', href: '/faturamento' }] },
  { title: 'Atendimento e vendas', links: [{ label: 'Caixa de entrada', href: '/caixa-de-entrada' }, { label: 'Funil de vendas', href: '/funil' }, { label: 'Painéis', href: '/paineis' }] },
  { title: 'Integrações', links: [{ label: 'API e webhooks', href: '/integracoes' }, { label: 'Chatwoot e Perfex', href: '/configuracoes?aba=integracoes' }] },
  { title: 'Conta e segurança', links: [{ label: 'Membros', href: '/usuarios' }, { label: 'Auditoria', href: '/auditoria' }, { label: 'Autenticação em dois fatores', href: '/perfil/autenticacao' }] },
];

function ActionCard({ icon: Ico, title, description, to, href, onClick, big }: {
  icon: Icon; title: string; description: string; to?: string; href?: string; onClick?: () => void; big?: boolean;
}) {
  const cls = `group flex flex-col gap-2 rounded-lg border border-kumo-line bg-kumo-base p-5 text-left no-underline transition-colors hover:border-kumo-strong ${big ? 'min-h-[120px]' : ''}`;
  const body = (
    <>
      <Ico size={22} className="text-kumo-subtle" />
      <div className="mt-auto flex items-center justify-between gap-2">
        <span className="text-base font-semibold text-kumo-default">{title}</span>
        <ArrowRight size={14} className="text-kumo-subtle transition-transform group-hover:translate-x-0.5" />
      </div>
      <span className="text-sm text-kumo-subtle">{description}</span>
    </>
  );
  if (to) return <Link to={to} className={cls}>{body}</Link>;
  if (href) return <a href={href} className={cls} target={href.startsWith('mailto:') ? undefined : '_blank'} rel="noopener noreferrer">{body}</a>;
  return <button type="button" onClick={onClick} className={cls}>{body}</button>;
}

export function SuportePage({ view }: { view?: 'casos' | 'novo' }) {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [form, setForm] = useState({ subject: '', description: '', category: params.get('categoria') === 'abuso' ? 'abuso' : 'geral', priority: 'normal' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchApi('/api/support/tickets');
      const j = await r.json();
      if (r.ok) setTickets(j.data ?? []);
    } catch { /* lista vazia */ } finally { setLoading(false); }
  }, [fetchApi]);
  useEffect(() => { void load(); }, [load]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const r = await fetchApi('/api/support/tickets', { method: 'POST', body: JSON.stringify(form) });
      const j = await r.json();
      if (!r.ok) { toast.error(j.message || 'Não foi possível abrir o chamado'); return; }
      toast.success(`Chamado #${j.data.number} aberto. Respondemos por e-mail.`);
      setForm({ subject: '', description: '', category: 'geral', priority: 'normal' });
      await load();
      navigate('/suporte/chamados');
    } catch { toast.error('Falha de rede ao abrir o chamado'); } finally { setSaving(false); }
  };

  const toggle = async (t: Ticket) => {
    const next = t.status === 'open' ? 'resolved' : 'open';
    const r = await fetchApi(`/api/support/tickets/${t.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
    if (r.ok) await load(); else toast.error('Não foi possível atualizar o chamado');
  };

  const shown = useMemo(() => tickets.filter((t) =>
    (status === 'all' || t.status === status) &&
    (!q.trim() || `${t.number} ${t.subject} ${t.description}`.toLowerCase().includes(q.trim().toLowerCase())),
  ), [tickets, q, status]);

  if (view === 'novo') {
    return (
      <FormPage
        backTo="/suporte"
        backLabel="Suporte"
        title="Abrir chamado"
        description="Conte o que está acontecendo. A equipe responde por e-mail."
        formId="ticket-form"
        submitLabel="Enviar chamado"
        submitting={saving}
        submitDisabled={form.subject.trim().length < 3 || form.description.trim().length < 10}
        onCancel={() => navigate('/suporte')}
      >
        <form id="ticket-form" onSubmit={submit} noValidate>
          <FormSection title="Detalhes">
            <div className="flex flex-col gap-4">
              <Field label="Assunto">
                <Input value={form.subject} maxLength={120} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Categoria">
                  <Select aria-label="Categoria" items={CATEGORIES} value={form.category} onValueChange={(v) => v && setForm({ ...form, category: v as string })} />
                </Field>
                <Field label="Prioridade">
                  <Select aria-label="Prioridade" items={PRIORITIES} value={form.priority} onValueChange={(v) => v && setForm({ ...form, priority: v as string })} />
                </Field>
              </div>
              <InputArea label="Descrição" description="Inclua o que você esperava, o que aconteceu e, se puder, a tela ou o horário." rows={8} value={form.description} maxLength={5000} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </FormSection>
        </form>
      </FormPage>
    );
  }

  if (view === 'casos') {
    return (
      <PageLayout
        title="Meus chamados"
        description="Acompanhe os chamados abertos e resolvidos da organização."
        actions={<Button variant="primary" onClick={() => navigate('/suporte/novo')}>Abrir chamado</Button>}
      >
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="w-72 max-w-full">
            <Input aria-label="Buscar chamados" placeholder="Buscar por número ou assunto" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="w-44">
            <Select aria-label="Status" items={[{ value: 'all', label: 'Todos' }, { value: 'open', label: 'Abertos' }, { value: 'resolved', label: 'Resolvidos' }]} value={status} onValueChange={(v) => v && setStatus(v as string)} />
          </div>
          <Link to="/suporte" className="ml-auto text-sm text-kumo-subtle hover:underline">← Portal de suporte</Link>
        </div>
        <DataTable<Ticket>
          items={shown}
          trackBy="id"
          loading={loading}
          loadingText="Carregando chamados"
          ariaLabels={{ tableLabel: 'Chamados de suporte' }}
          empty={<div className="py-8 text-center text-sm text-kumo-subtle">{tickets.length ? 'Nenhum chamado com esse filtro.' : 'Você ainda não abriu nenhum chamado.'}</div>}
          columnDefinitions={[
            { id: 'n', header: 'Nº', width: 70, cell: (t) => `#${t.number}` },
            { id: 's', header: 'Assunto', cell: (t) => <span className="font-medium">{t.subject}</span> },
            { id: 'c', header: 'Categoria', cell: (t) => label(CATEGORIES, t.category) },
            { id: 'p', header: 'Prioridade', cell: (t) => label(PRIORITIES, t.priority) },
            { id: 'st', header: 'Status', cell: (t) => <Badge variant={t.status === 'open' ? 'primary' : 'secondary'}>{t.status === 'open' ? 'Aberto' : 'Resolvido'}</Badge> },
            { id: 'd', header: 'Aberto em', cell: (t) => new Date(t.createdAt).toLocaleDateString('pt-BR') },
            { id: 'a', header: '', width: 130, cell: (t) => <Button size="sm" variant="ghost" onClick={() => toggle(t)}>{t.status === 'open' ? 'Marcar resolvido' : 'Reabrir'}</Button> },
          ]}
        />
      </PageLayout>
    );
  }

  const open = tickets.filter((t) => t.status === 'open').length;
  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-8">
      <div className="mb-8 flex justify-center">
        <Banner variant="default" className="max-w-md">
          <span className="text-kumo-default">Status do sistema: todos os serviços operacionais</span>
        </Banner>
      </div>
      <h1 className="mb-8 text-center text-3xl font-semibold text-kumo-default">Como podemos ajudar?</h1>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ActionCard big icon={Tray} to="/suporte/chamados" title="Meus chamados" description={open ? `${open} chamado(s) em aberto. Veja e gerencie os seus chamados.` : 'Veja e gerencie os chamados abertos e resolvidos.'} />
        <ActionCard big icon={Lifebuoy} to="/suporte/novo" title="Abrir chamado" description="Crie um chamado para o seu problema." />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <ActionCard icon={Sparkle} to="/dashboard" title="Perguntar à Lumi" description="Respostas na hora com o assistente de IA." />
        <ActionCard icon={Flag} to="/suporte/novo?categoria=abuso" title="Denunciar abuso" description="Abra um chamado de denúncia." />
        <ActionCard icon={UsersThree} href="mailto:suporte@ingrave.com.br" title="Falar com a equipe" description="Envie um e-mail direto ao suporte." />
      </div>

      <section className="mt-12 rounded-lg border border-kumo-line bg-kumo-base">
        <div className="border-b border-kumo-line p-6">
          <Badge variant="secondary">Base de conhecimento</Badge>
          <h2 className="mt-3 text-2xl font-semibold text-kumo-default">Navegue por tópico</h2>
          <p className="mt-1 text-sm text-kumo-subtle">Guias e descrições dos recursos da plataforma.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {TOPICS.map((t) => (
            <div key={t.title} className="border-b border-kumo-line p-6 lg:border-r lg:[&:nth-child(3n)]:border-r-0">
              <h3 className="mb-3 text-base font-semibold text-kumo-default">{t.title}</h3>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {t.links.map((l) => (
                  <li key={l.label}>
                    {l.href === '/docs'
                      ? <a href={l.href} target="_blank" rel="noopener noreferrer" className="text-sm text-kumo-subtle no-underline hover:text-kumo-default hover:underline">{l.label}</a>
                      : <Link to={l.href} className="text-sm text-kumo-subtle no-underline hover:text-kumo-default hover:underline">{l.label}</Link>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
